import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '../lib/cn.js';
import { Icon } from './Icon.js';

export interface AlbumPickerOption {
  id: string;
  name: string;
  // Breadcrumb of ancestor names (root → leaf, excluding the leaf itself).
  // Rendered as a thin secondary line so the picker disambiguates two
  // sub-albums named "Mare" sitting under different roots.
  breadcrumb: string[];
}

export interface AlbumPickerComboboxProps {
  options: AlbumPickerOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  // Minimum characters before filtering kicks in. Below this threshold the
  // dropdown surfaces "recents" / first N options as a discoverability hint.
  minChars?: number;
  // Cap rendered results to keep the dropdown scannable.
  maxResults?: number;
  disabled?: boolean;
  // Optional empty-state copy when no options match.
  emptyLabel?: string;
}

const DEFAULT_MIN_CHARS = 2;
const DEFAULT_MAX_RESULTS = 20;

// Searchable combobox over a pre-decrypted album list. Filtering is purely
// client-side — the server never sees plaintext names. Up/Down/Enter to
// navigate; Esc to close. Designed to feel like a photo-app destination
// picker (folder icon + name + path) rather than an admin <select>.
export function AlbumPickerCombobox({
  options,
  value,
  onChange,
  placeholder = 'Cerca un album…',
  minChars = DEFAULT_MIN_CHARS,
  maxResults = DEFAULT_MAX_RESULTS,
  disabled,
  emptyLabel = 'Nessun album corrisponde',
}: AlbumPickerComboboxProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listboxId = useId();

  const selected = useMemo(
    () => (value ? (options.find((o) => o.id === value) ?? null) : null),
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < minChars) {
      // Below threshold: show first N as discoverability — sorted by name
      // so the list is scannable.
      return [...options].sort((a, b) => a.name.localeCompare(b.name)).slice(0, maxResults);
    }
    const matched = options
      .map((o) => {
        const inName = o.name.toLowerCase().includes(q);
        const inPath = o.breadcrumb.some((b) => b.toLowerCase().includes(q));
        if (!inName && !inPath) return null;
        // Rank: name match > path match; among same rank, shorter name first.
        return { o, score: (inName ? 0 : 1) * 100 + o.name.length };
      })
      .filter((x): x is { o: AlbumPickerOption; score: number } => x !== null)
      .sort((a, b) => a.score - b.score)
      .slice(0, maxResults)
      .map((x) => x.o);
    return matched;
  }, [options, query, minChars, maxResults]);

  useEffect(() => {
    setActiveIdx(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    function onClickAway(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [open]);

  function commit(opt: AlbumPickerOption) {
    onChange(opt.id);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  }

  function clear() {
    onChange(null);
    setQuery('');
    inputRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      const opt = filtered[activeIdx];
      if (opt) {
        e.preventDefault();
        commit(opt);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapRef} className="relative">
      <div
        className={cn(
          'flex items-center gap-3 rounded-xl border bg-surface-container-lowest px-4 py-3.5 transition-all duration-150',
          open
            ? 'border-cyan ring-4 ring-cyan/15'
            : 'border-outline hover:border-on-surface-variant/40',
          disabled && 'opacity-50',
        )}
      >
        <Icon name="search" className="text-on-surface-variant" />
        {selected && !open ? (
          <button
            type="button"
            onClick={() => {
              setOpen(true);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            disabled={disabled}
          >
            <Icon name="folder" className="text-cyan" filled />
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-[15px] text-on-surface">{selected.name}</span>
              {selected.breadcrumb.length > 0 ? (
                <span className="truncate text-xs text-on-surface-variant">
                  {selected.breadcrumb.join(' › ')}
                </span>
              ) : null}
            </span>
          </button>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-activedescendant={
              open && filtered[activeIdx] ? `${listboxId}-opt-${activeIdx}` : undefined
            }
            className="min-w-0 flex-1 bg-transparent text-[15px] text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none"
          />
        )}
        {selected ? (
          <button
            type="button"
            onClick={clear}
            disabled={disabled}
            aria-label="Rimuovi selezione"
            className="rounded-full p-1 text-on-surface-variant transition-colors hover:bg-surface-container-low hover:text-on-surface"
          >
            <Icon name="close" className="text-base" />
          </button>
        ) : null}
      </div>

      {open && !disabled ? (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-30 mt-2 max-h-72 overflow-auto rounded-xl border border-outline bg-surface-container shadow-elevated"
        >
          {filtered.length === 0 ? (
            <li className="px-4 py-6 text-center text-sm text-on-surface-variant">
              {query.trim().length < minChars ? `Digita almeno ${minChars} caratteri` : emptyLabel}
            </li>
          ) : (
            filtered.map((opt, i) => (
              <li
                key={opt.id}
                id={`${listboxId}-opt-${i}`}
                role="option"
                aria-selected={i === activeIdx}
              >
                <button
                  type="button"
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => commit(opt)}
                  className={cn(
                    'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
                    i === activeIdx ? 'bg-surface-container-low' : 'hover:bg-surface-container-low',
                  )}
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan/10 text-cyan">
                    <Icon name="folder" filled />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm text-on-surface">{opt.name}</span>
                    {opt.breadcrumb.length > 0 ? (
                      <span className="truncate text-xs text-on-surface-variant">
                        {opt.breadcrumb.join(' › ')}
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
